import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  getLabels,
  GPanelContactsKeys,
} from '../components/directory/gpanel-contacts-v2/gpanel-contacts-api';
import { UseFormSetValue } from 'react-hook-form';
import { LabelSync } from '../components/directory/gpanel-contacts-v2/shared/types';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LabelIcon from '@mui/icons-material/Label';

interface LabelsAutocompleteProps {
  value: string;
  name: keyof Partial<LabelSync>;
  setValue: UseFormSetValue<Partial<LabelSync>>;
  setSelectedLabelId?: (value: number) => void;
  setNewLabelId?: (value: number | undefined) => void;
  error?: boolean;
  onChange?: (value: string) => void;
  disableOnEdit?: boolean;
}

export default function LabelsAutocomplete({
  value,
  setValue,
  setSelectedLabelId,
  setNewLabelId,
  error,
  name,
  onChange,
  disableOnEdit = false,
}: LabelsAutocompleteProps) {
  const { data: labelData, isFetching: labelLoading } = useQuery({
    queryKey: [GPanelContactsKeys.GPanelLabels],
    queryFn: () => getLabels(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
  });

  interface Option {
    id: number;
    name: string;
    trashed: boolean | null;
    count: number;
  }

  const labelOptions: Option[] =
    labelData
      ?.filter((label) => !label.trashed)
      .map((label) => ({
        id: label.id,
        name: label.name,
        trashed: label.trashed,
        count: label.count ?? 0,
      })) || [];

  // Find the selected option from the value
  const selectedOption =
    labelOptions.find((option) => option.name === value) || null;

  return (
    <Autocomplete
      disabled={labelOptions.length === 0 || labelLoading || disableOnEdit}
      options={labelOptions}
      autoComplete
      autoHighlight
      selectOnFocus
      noOptionsText="No Labels Found"
      getOptionLabel={(option) => option.name}
      sx={{ width: 400 }}
      value={selectedOption}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(_event, newValue) => {
        if (!newValue) {
          setValue(name, '');
          onChange?.('');
          setSelectedLabelId?.(-1);
          setNewLabelId?.(-1);
          return;
        }
        setValue(name, newValue.name);
        onChange?.(newValue.name);
        setSelectedLabelId?.(newValue.id);
        setNewLabelId?.(newValue.id);
      }}
      renderOption={(props, option) => (
        <li
          {...props}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LabelIcon fontSize="small" color="disabled" />
            {option.name}
          </div>
          <Typography className="count" color="textSecondary">
            {option.count}
          </Typography>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          label="Source Label"
          error={error}
          helperText={error ? 'Source Label is required.' : ' '}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {labelLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
